import { useStorageDao } from "@/dao/storage-dao";
import { authStorage } from "@/plugins/nhost";

const storageDao = useStorageDao();

const ACCESS_TOKEN = "access_token";
const REFRESH_TOKEN = "refresh_token";
const USER_ID = "user_id";
const WHATS_NEW_SEEN_CONTENT = "whats_new_seen_content";
const LAST_ACTIVE = "last_active";
const FIRST_SIGN_IN = "first_sign_in";
const CHATBOT_STATE = "chatbot_state";
const CHATBOT_MENTION_HINT_DISMISSED = "chatbot_mention_hint_dismissed";

function getAccessToken(): string | null {
  return storageDao.get(ACCESS_TOKEN) || null;
}

function setAccessToken(value: string) {
  storageDao.set(ACCESS_TOKEN, value);
}

function getRefreshToken(): string | null {
  return storageDao.get(REFRESH_TOKEN);
}

function setRefreshToken(value: string) {
  storageDao.set(REFRESH_TOKEN, value);
}

function setUserId(value: string) {
  storageDao.set(USER_ID, value);
}

function setLastActive(value: string) {
  storageDao.set(LAST_ACTIVE, value);
}

function setFirstSignIn(value: string){
  return storageDao.set(FIRST_SIGN_IN, value);
}

function removeAccessToken() {
  storageDao.remove(ACCESS_TOKEN);
}

function removeRefreshToken() {
  storageDao.remove(REFRESH_TOKEN);
}

function removeUserId() {
  storageDao.remove(USER_ID);
}

function removeLastActive() {
  storageDao.remove(LAST_ACTIVE);
}

function removeFirstSignIn() {
  storageDao.remove(FIRST_SIGN_IN);
}

function getUserId(): string | null {
  return storageDao.get(USER_ID);
}

function getLinkFromUid(fileId: string) {
  return authStorage.getPublicUrl({ fileId });
}

function getFirstSignIn(){
  return storageDao.get(FIRST_SIGN_IN);
}

function getLastActive(){
  return storageDao.get(LAST_ACTIVE);
}

function getWhatsNewSeenContent(): string {
  return storageDao.get(WHATS_NEW_SEEN_CONTENT);
}

function setWhatsNewSeenContent(value: string) {
  storageDao.set(WHATS_NEW_SEEN_CONTENT, `${value}`);
}

async function getFile(uuid: string, name?: string) {
  const res = await fetch(getLinkFromUid(uuid), {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });

  const blob = await res.blob();
  const file = new File([blob], name || "image", {
    type: blob.type,
  });
  return file;
}

function uploadFile(file: File) {
  return authStorage.upload({ file });
}

function deleteFile(fileId: string) {
  return authStorage.delete({ fileId });
}

function setToken(token: string) {
  return authStorage.setAccessToken(token);
}

function getChatbotState<T>(): T | null {
  const raw = storageDao.get(CHATBOT_STATE);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function setChatbotState(state: unknown) {
  storageDao.set(CHATBOT_STATE, JSON.stringify(state));
}

function removeChatbotState() {
  storageDao.remove(CHATBOT_STATE);
}

function getChatbotMentionHintDismissed(): boolean {
  return storageDao.get(CHATBOT_MENTION_HINT_DISMISSED) === "true";
}

function setChatbotMentionHintDismissed() {
  storageDao.set(CHATBOT_MENTION_HINT_DISMISSED, "true");
}

export function useStorageService() {
  return {
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    setUserId,
    removeAccessToken,
    removeRefreshToken,
    removeUserId,
    getUserId,
    getLinkFromUid,
    uploadFile,
    setToken,
    getFile,
    deleteFile,
    getWhatsNewSeenContent,
    setWhatsNewSeenContent,
    setLastActive,
    removeLastActive,
    getLastActive,
    getFirstSignIn,
    setFirstSignIn,
    removeFirstSignIn,
    getChatbotState,
    setChatbotState,
    removeChatbotState,
    getChatbotMentionHintDismissed,
    setChatbotMentionHintDismissed,
  };
}
